import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { axiosInstance } from '../App';
import { GraduationCap, PlayCircle, X } from 'lucide-react';

const EducaEmpresarioPage = ({ user, onLogout }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/educa-empresario');
      setVideos(response.data);
    } catch (error) {
      console.error('Erro ao carregar vídeos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
    return url;
  };

  const getVideoThumbnail = (url) => {
    const youtubeMatch = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
    }
    return null;
  };

  // Agrupar vídeos por categoria
  const categorias = [...new Set(videos.map(v => v.categoria || 'Geral'))];

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} />

      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-600 to-orange-600">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Educa Empresário</h1>
              <p className="text-zinc-400">Aprenda sobre gestão, finanças e tributos para fazer sua empresa crescer</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin"></div>
            </div>
          ) : videos.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-16 text-center">
                <GraduationCap className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                <h3 className="text-xl font-semibold text-zinc-400 mb-2">Nenhum vídeo disponível</h3>
                <p className="text-zinc-500">Em breve teremos conteúdos educacionais para você!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {categorias.map((categoria) => (
                <div key={categoria}>
                  <h2 className="text-xl font-semibold text-amber-400 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                    {categoria}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {videos
                      .filter(v => (v.categoria || 'Geral') === categoria)
                      .map((video) => (
                        <Card 
                          key={video.id} 
                          className="bg-zinc-900 border-zinc-800 hover:border-amber-500/50 transition-all cursor-pointer group"
                          onClick={() => setActiveVideo(video)}
                        >
                          <div className="relative aspect-video bg-zinc-800 rounded-t-lg overflow-hidden">
                            {getVideoThumbnail(video.url) ? (
                              <img 
                                src={getVideoThumbnail(video.url)} 
                                alt={video.titulo}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <PlayCircle className="w-16 h-16 text-zinc-600" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center">
                                <PlayCircle className="w-8 h-8 text-white" />
                              </div>
                            </div>
                          </div>
                          <CardContent className="p-4">
                            <h3 className="font-semibold text-white group-hover:text-amber-400 transition-colors">
                              {video.titulo}
                            </h3>
                            {video.descricao && (
                              <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{video.descricao}</p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal do Player de Vídeo */}
      <Dialog open={!!activeVideo} onOpenChange={(open) => !open && setActiveVideo(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 bg-gradient-to-r from-amber-900/80 to-orange-900/80">
            <DialogTitle className="text-white flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-amber-400" />
              {activeVideo?.titulo}
            </DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black">
            {activeVideo && (
              <iframe
                src={getEmbedUrl(activeVideo.url)}
                title={activeVideo.titulo}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
          {activeVideo?.descricao && (
            <div className="p-4 border-t border-zinc-800">
              <p className="text-zinc-300 text-sm">{activeVideo.descricao}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducaEmpresarioPage;
